import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Filter,
  Layers,
  KeyRound,
  Code2,
  Copy,
  Check,
  Zap,
  ArrowRight,
  Database,
  Calendar,
  Sparkles,
  Search,
  TrendingUp,
  AlertTriangle,
  ListChecks,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API_BASE_URL =
  typeof window !== "undefined"
    ? `${window.location.origin}/api/v1`
    : "https://api.dotuscrm.com.br/v1";
const API_KEY_EXAMPLE = "<SUA_API_KEY>";

// Aponta para o proxy corporativo do próprio CRM (/api/v1) sem expor a URL interna do Supabase
const SUPABASE_URL = API_BASE_URL;
const SUPABASE_ANON_KEY = API_KEY_EXAMPLE;

// ─── Helper de Cópia ─────────────────────────────────────────────────────────

const CodeBlock = ({
  code,
  language = "http",
  title,
  playgroundUrl,
}: {
  code: string;
  language?: string;
  title?: string;
  playgroundUrl?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="rounded-lg border border-border/60 bg-zinc-950 overflow-hidden my-3">
      {(title || playgroundUrl) && (
        <div className="flex items-center justify-between px-3.5 py-2 border-b border-border/40 bg-zinc-900/60 text-xs">
          <span className="font-mono text-zinc-400 font-medium">
            {title || language}
          </span>
          <div className="flex items-center gap-2">
            {playgroundUrl && (
              <Button
                variant="ghost"
                size="sm"
                asChild
                className="h-6 px-2 text-[11px] text-violet-400 hover:text-violet-300"
              >
                <Link to={playgroundUrl}>
                  <Zap className="h-3 w-3 mr-1" />
                  Testar no Playground
                </Link>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-6 px-2 text-[11px] text-zinc-400 hover:text-zinc-200"
            >
              {copied ? (
                <>
                  <Check className="h-3 w-3 text-emerald-400 mr-1" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="h-3 w-3 mr-1" />
                  Copiar
                </>
              )}
            </Button>
          </div>
        </div>
      )}
      <div className="relative">
        {!title && !playgroundUrl && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="absolute top-2 right-2 h-7 w-7 p-0 z-10 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-emerald-400" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
        <pre className="p-4 text-xs font-mono text-zinc-200 overflow-x-auto leading-relaxed whitespace-pre-wrap">
          {code}
        </pre>
      </div>
    </div>
  );
};

// ─── Componente Principal de Documentação ────────────────────────────────────

export const ApiDocs = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const OPERATORS = [
    {
      op: "eq",
      name: "Igual (Equals)",
      syntax: "campo=eq.valor",
      example: "status=eq.hot",
      desc: "Retorna registros cujo campo é exatamente igual ao valor fornecido.",
    },
    {
      op: "neq",
      name: "Diferente (Not Equals)",
      syntax: "campo=neq.valor",
      example: "stage=neq.archived",
      desc: "Retorna registros cujo campo é diferente do valor.",
    },
    {
      op: "gt",
      name: "Maior que (Greater Than)",
      syntax: "campo=gt.valor",
      example: "amount=gt.5000",
      desc: "Compara números ou datas estritamente maiores que o valor.",
    },
    {
      op: "gte",
      name: "Maior ou igual (Greater Than or Equal)",
      syntax: "campo=gte.valor",
      example: "amount=gte.10000",
      desc: "Compara números ou datas maiores ou iguais.",
    },
    {
      op: "lt",
      name: "Menor que (Less Than)",
      syntax: "campo=lt.valor",
      example: "due_date=lt.2026-10-01T00:00:00Z",
      desc: "Compara números ou datas estritamente menores que o valor.",
    },
    {
      op: "lte",
      name: "Menor ou igual (Less Than or Equal)",
      syntax: "campo=lte.valor",
      example: "due_date=lte.2026-10-31T23:59:59Z",
      desc: "Compara números ou datas menores ou iguais.",
    },
    {
      op: "like",
      name: "Busca de texto com padrão (Case-Sensitive)",
      syntax: "campo=like.*termo*",
      example: "name=like.*Tech*",
      desc: "Busca por padrão usando asterisco (*) como curinga. Sensível a maiúsculas.",
    },
    {
      op: "ilike",
      name: "Busca de texto sem diferenciar maiúsculas",
      syntax: "campo=ilike.*termo*",
      example: "first_name=ilike.*joao*",
      desc: "Busca case-insensitive. Ideal para buscas em nomes e descrições.",
    },
    {
      op: "is",
      name: "Checagem nula ou booleana",
      syntax: "campo=is.null / campo=is.true",
      example: "done_date=is.null",
      desc: "Verifica valores nulos ou booleanos. Use not.is.null para checar preenchimento.",
    },
    {
      op: "in",
      name: "Lista de valores permitidos (IN)",
      syntax: 'campo=in.("val1","val2")',
      example: "stage=in.(opportunity,proposal-sent,in-negotiation)",
      desc: "Filtra registros cujo valor esteja dentro da lista entre parênteses.",
    },
    {
      op: "or",
      name: "Disjunção Lógica (OR)",
      syntax: "or=(cond1,cond2)",
      example: "or=(status.eq.hot,category.eq.VIP)",
      desc: "Retorna registros que satisfaçam ao menos uma das condições agrupadas.",
    },
  ];

  const filteredOperators = OPERATORS.filter(
    (o) =>
      o.op.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.desc.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6 pb-16 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/40 pb-5">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-violet-500/15 border border-violet-500/30">
              <BookOpen className="h-5 w-5 text-violet-400" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">
              Guia da API REST & Referência Técnica
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Documentação completa de filtros, modificadores, paginação,
            relacionamentos e campos customizados (JSONB).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="gap-1.5 border-violet-500/30 hover:bg-violet-500/10"
          >
            <Link to="/api-playground">
              <Zap className="h-4 w-4 text-violet-400" />
              Abrir API Playground
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="gap-1.5 text-muted-foreground"
          >
            <Link to="/profile">
              <KeyRound className="h-4 w-4" />
              Minhas API Keys
            </Link>
          </Button>
        </div>
      </div>

      {/* Navegação por Abas */}
      <Tabs defaultValue="filtros" className="space-y-6">
        <TabsList className="bg-muted/40 p-1 border border-border/40 flex-wrap h-auto gap-1">
          <TabsTrigger value="filtros" className="gap-1.5 text-xs sm:text-sm">
            <Filter className="h-4 w-4" />
            Operadores de Filtro
          </TabsTrigger>
          <TabsTrigger
            value="modificadores"
            className="gap-1.5 text-xs sm:text-sm"
          >
            <Layers className="h-4 w-4" />
            Modificadores (Paginação e Ordem)
          </TabsTrigger>
          <TabsTrigger value="funis" className="gap-1.5 text-xs sm:text-sm">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            Funis & Etapas
          </TabsTrigger>
          <TabsTrigger
            value="atividades"
            className="gap-1.5 text-xs sm:text-sm"
          >
            <Calendar className="h-4 w-4 text-amber-400" />
            Atividades & JOINs
          </TabsTrigger>
          <TabsTrigger
            value="custom-fields"
            className="gap-1.5 text-xs sm:text-sm"
          >
            <Sparkles className="h-4 w-4 text-violet-400" />
            Custom Fields & Identificadores
          </TabsTrigger>
          <TabsTrigger value="recursos" className="gap-1.5 text-xs sm:text-sm">
            <Database className="h-4 w-4" />
            Catálogo de Recursos
          </TabsTrigger>
          <TabsTrigger
            value="autenticacao"
            className="gap-1.5 text-xs sm:text-sm"
          >
            <KeyRound className="h-4 w-4" />
            Autenticação
          </TabsTrigger>
        </TabsList>

        {/* ─── ABA 1: OPERADORES DE FILTRO ──────────────────────────────────── */}
        <TabsContent value="filtros" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Filter className="h-5 w-5 text-violet-400" />
                    Como funcionam os Filtros no PostgREST
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Os filtros são passados como parâmetros na URL após o{" "}
                    <code className="text-violet-400">?</code> utilizando o
                    formato{" "}
                    <code className="text-violet-400">
                      coluna=operador.valor
                    </code>
                    .
                  </CardDescription>
                </div>
                <div className="relative w-full sm:w-64">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Buscar operador..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-9 text-xs"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {filteredOperators.map((item) => (
                  <div
                    key={item.op}
                    className="p-3.5 rounded-lg border border-border/50 bg-muted/20 hover:bg-muted/40 transition-colors space-y-2"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="font-mono text-violet-400 border-violet-500/30 bg-violet-500/10 text-xs font-bold px-2 py-0.5"
                        >
                          {item.op}
                        </Badge>
                        <span className="font-semibold text-sm text-foreground">
                          {item.name}
                        </span>
                      </div>
                      <code className="text-[11px] font-mono bg-zinc-950 px-2 py-0.5 rounded border border-border/40 text-amber-300">
                        {item.syntax}
                      </code>
                    </div>

                    <p className="text-xs text-muted-foreground">{item.desc}</p>

                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-border/30">
                      <span className="text-[11px] text-zinc-400 flex items-center gap-1 font-mono">
                        <span className="text-emerald-400 font-semibold">
                          Exemplo:
                        </span>{" "}
                        ?{item.example}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-6 px-2 text-[11px] text-violet-400 hover:text-violet-300"
                      >
                        <Link to={`/api-playground`}>
                          Testar <ArrowRight className="h-3 w-3 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Combinação de Filtros (AND / OR) */}
              <div className="mt-6 p-4 rounded-xl border border-violet-500/20 bg-violet-950/10 space-y-3">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-violet-400" />
                  Combinando Múltiplos Filtros (Operador E / AND)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Para aplicar várias condições simultaneamente (lógica{" "}
                  <strong>AND</strong>), basta concatenar os parâmetros com{" "}
                  <code className="text-violet-400">&amp;</code>:
                </p>
                <CodeBlock
                  title="Exemplo: Negócios na etapa 'opportunity' com valor acima de R$ 10.000"
                  code={`GET ${SUPABASE_URL}/rest/v1/deals?stage=eq.opportunity&amount=gte.10000&order=amount.desc`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Filtro Lógico OR */}
              <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-950/10 space-y-3">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-amber-400" />
                  Filtro Condicional OU (Operador OR)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Para buscar registros que satisfaçam a Condição A{" "}
                  <strong>OU</strong> a Condição B, use a função{" "}
                  <code className="text-amber-400">or=(...)</code>:
                </p>
                <CodeBlock
                  title="Exemplo: Contatos que sejam status 'hot' OU tenham newsletter ativa"
                  code={`GET ${SUPABASE_URL}/rest/v1/contacts?or=(status.eq.hot,has_newsletter.eq.true)&limit=20`}
                  playgroundUrl="/api-playground"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA 2: MODIFICADORES ─────────────────────────────────────────── */}
        <TabsContent value="modificadores" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Paginação */}
            <Card className="border-border/60 bg-card/60 md:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Layers className="h-4 w-4 text-blue-400" />
                  Paginação
                </CardTitle>
                <CardDescription className="text-xs">
                  Controle a quantidade de registros retornados.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div>
                  <span className="font-mono text-violet-400 font-bold">
                    limit=N
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Define o número máximo de registros retornados.
                  </p>
                </div>
                <div>
                  <span className="font-mono text-violet-400 font-bold">
                    offset=N
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Pula N registros (ideal para paginação baseada em páginas).
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/40 font-mono text-[11px] space-y-1">
                  <p className="text-zinc-400">// Página 1 (25 registros):</p>
                  <p className="text-foreground">?limit=25&offset=0</p>
                  <p className="text-zinc-400">// Página 2:</p>
                  <p className="text-foreground">?limit=25&offset=25</p>
                </div>
              </CardContent>
            </Card>

            {/* Ordenação */}
            <Card className="border-border/60 bg-card/60 md:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-emerald-400" />
                  Ordenação (order)
                </CardTitle>
                <CardDescription className="text-xs">
                  Ordene por uma ou várias colunas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div>
                  <span className="font-mono text-emerald-400 font-bold">
                    order=coluna.asc
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Ordenação crescente (A-Z ou menor para maior).
                  </p>
                </div>
                <div>
                  <span className="font-mono text-emerald-400 font-bold">
                    order=coluna.desc
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Ordenação decrescente (Z-A ou maior para menor).
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/40 font-mono text-[11px] space-y-1">
                  <p className="text-zinc-400">// Mais recentes primeiro:</p>
                  <p className="text-foreground">?order=created_at.desc</p>
                  <p className="text-zinc-400">// Nulos no final:</p>
                  <p className="text-foreground">
                    ?order=due_date.asc.nullslast
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Seleção de Colunas */}
            <Card className="border-border/60 bg-card/60 md:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-amber-400" />
                  Seleção de Colunas (select)
                </CardTitle>
                <CardDescription className="text-xs">
                  Traga somente os campos necessários.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <div>
                  <span className="font-mono text-amber-400 font-bold">
                    select=col1,col2
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Otimiza a banda de rede trazendo apenas os campos
                    solicitados.
                  </p>
                </div>
                <div>
                  <span className="font-mono text-amber-400 font-bold">
                    select=alias:coluna
                  </span>
                  <p className="text-muted-foreground mt-0.5">
                    Renomeia campos no JSON de saída.
                  </p>
                </div>
                <div className="p-2.5 rounded bg-muted/40 font-mono text-[11px] space-y-1">
                  <p className="text-zinc-400">// Apenas id, nome e valor:</p>
                  <p className="text-foreground">?select=id,name,amount</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-border/60 bg-card/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">
                Exemplo Completo: Consulta com Paginação, Ordem e Filtro
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CodeBlock
                title="Buscar os 10 negócios mais valiosos criados recentemente"
                code={`GET ${SUPABASE_URL}/rest/v1/deals?select=id,name,amount,stage,created_at&stage=neq.archived&order=amount.desc,created_at.desc&limit=10&offset=0`}
                playgroundUrl="/api-playground"
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA NOVO: FUNIS & ETAPAS ──────────────────────────────────── */}
        <TabsContent value="funis" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                API de Funis (Pipelines) e Etapas (Stages)
              </CardTitle>
              <CardDescription className="text-xs">
                Endpoints completos para listar, criar, editar e excluir funis e
                suas respectivas etapas, além de movimentação e métricas de
                negócios.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* 1. FUNIS */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  >
                    Funis
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    1. Gerenciar Funis (Pipelines) — GET, POST, PATCH, DELETE
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Acesse a tabela{" "}
                  <code className="text-emerald-400 font-mono">pipelines</code>{" "}
                  para gerenciar múltiplos funis de vendas:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-emerald-400 font-bold">
                      // Listar todos os funis:
                    </p>
                    <p>GET /pipelines?order=order_nr.asc</p>
                    <p className="text-emerald-400 font-bold mt-2">
                      // Buscar funil por ID com suas etapas:
                    </p>
                    <p>GET /pipelines?id=eq.1&select=*,pipeline_stages(*)</p>
                  </div>
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-blue-400 font-bold">
                      // Atualizar nome/ordem:
                    </p>
                    <p>PATCH /pipelines?id=eq.1</p>
                    <p className="text-red-400 font-bold mt-2">
                      // Excluir funil:
                    </p>
                    <p>DELETE /pipelines?id=eq.1</p>
                  </div>
                </div>

                <CodeBlock
                  language="json"
                  title="Criar novo funil (POST /pipelines)"
                  code={`POST ${SUPABASE_URL}/rest/v1/pipelines
Content-Type: application/json

{
  "name": "Funil B2B Enterprise",
  "description": "Pipeline comercial para grandes contas e contratos anuais",
  "is_default": false,
  "order_nr": 2
}`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* 2. ETAPAS */}
              <div className="space-y-3 border-t border-border/40 pt-6">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-violet-500/10 text-violet-400 border-violet-500/30"
                  >
                    Etapas
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    2. Gerenciar Etapas (pipeline_stages) — GET, POST, PATCH,
                    DELETE
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  As etapas pertencem a um funil (
                  <code className="text-violet-400 font-mono">pipeline_id</code>
                  ) e possuem ordenação por{" "}
                  <code className="text-violet-400 font-mono">order_nr</code>,{" "}
                  <code className="text-violet-400 font-mono">id</code> ou data:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-violet-400 font-bold">
                      // Listar etapas de um funil:
                    </p>
                    <p>
                      GET /pipeline_stages?pipeline_id=eq.1&order=order_nr.asc
                    </p>
                    <p className="text-violet-400 font-bold mt-2">
                      // Buscar etapa por ID:
                    </p>
                    <p>GET /pipeline_stages?id=eq.5</p>
                  </div>
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-blue-400 font-bold">
                      // Atualizar nome, cor ou ordem:
                    </p>
                    <p>PATCH /pipeline_stages?id=eq.5</p>
                    <p className="text-red-400 font-bold mt-2">
                      // Excluir etapa:
                    </p>
                    <p>DELETE /pipeline_stages?id=eq.5</p>
                  </div>
                </div>

                <CodeBlock
                  language="json"
                  title="Criar nova etapa no funil (POST /pipeline_stages)"
                  code={`POST ${SUPABASE_URL}/rest/v1/pipeline_stages
Content-Type: application/json

{
  "pipeline_id": 1,
  "name": "Diagnóstico Inicial",
  "slug": "diagnostico-inicial",
  "order_nr": 1,
  "color": "#3b82f6"
}`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* 3. NEGÓCIOS DENTRO DO FUNIL */}
              <div className="space-y-3 border-t border-border/40 pt-6">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-amber-500/10 text-amber-400 border-amber-500/30"
                  >
                    Negócios no Funil
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    3. Criar, Mover e Medir Métricas de Negócios por Funil &
                    Etapa
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ao criar ou atualizar um negócio na tabela{" "}
                  <code className="text-amber-400 font-mono">deals</code>, você
                  define o funil (
                  <code className="text-amber-400 font-mono">funil_id</code>) e
                  a etapa (
                  <code className="text-amber-400 font-mono">stage</code>):
                </p>

                <CodeBlock
                  language="json"
                  title="Criar negócio já atribuído ao funil e etapa (POST /deals)"
                  code={`POST ${SUPABASE_URL}/rest/v1/deals
Content-Type: application/json

{
  "name": "Contrato Anual - Grupo Alpha",
  "funil_id": 1,
  "stage": "diagnostico-inicial",
  "amount": 25000,
  "company_id": 1,
  "custom_fields": {
    "origem_lead": "indicacao"
  }
}`}
                  playgroundUrl="/api-playground"
                />

                <CodeBlock
                  language="json"
                  title="Mover negócio de etapa ou de funil (PATCH /deals?id=eq.10)"
                  code={`PATCH ${SUPABASE_URL}/rest/v1/deals?id=eq.10
Content-Type: application/json

{
  "stage": "proposta-enviada",
  "funil_id": 1
}`}
                  playgroundUrl="/api-playground"
                />

                <CodeBlock
                  title="Métricas de conversão: Listar negócios de uma etapa específica do funil"
                  code={`GET ${SUPABASE_URL}/rest/v1/deals?funil_id=eq.1&stage=eq.proposta-enviada&select=id,name,amount,created_at`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* 4. CONTAGEM DE LEADS POR ETAPA */}
              <div className="space-y-3 border-t border-border/40 pt-6">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  >
                    Contagem & Métricas
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    4. Contagem de Leads por Etapa (View Resumo ou Header de
                    Contagem)
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Para obter a quantidade de leads em cada etapa do funil (e o
                  valor total acumulado), basta consultar o próprio endpoint{" "}
                  <code>/pipeline_stages</code>:
                </p>

                {/* Endpoint pipeline_stages com contagem automática */}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Cada etapa retorna automaticamente a contagem total (
                    <code className="text-foreground font-mono">
                      leads_count
                    </code>
                    ), contagem de abertos (
                    <code className="text-foreground font-mono">
                      leads_open_count
                    </code>
                    ) e soma de valores (
                    <code className="text-foreground font-mono">
                      total_amount
                    </code>
                    ):
                  </p>
                  <CodeBlock
                    title="Obter etapas do funil com contagem de leads em tempo real"
                    code={`GET ${SUPABASE_URL}/rest/v1/pipeline_stages?pipeline_id=eq.375&order=order_nr.asc`}
                    playgroundUrl="/api-playground"
                  />
                  <CodeBlock
                    language="json"
                    title="Exemplo de resposta da tabela pipeline_stages"
                    code={`[
  {
    "id": 2010,
    "pipeline_id": 375,
    "name": "Entrada de Prospect",
    "slug": "entrada-de-prospect",
    "order_nr": 1,
    "color": "#6366f1",
    "leads_count": 2,
    "leads_open_count": 2,
    "total_amount": 250.00
  },
  {
    "id": 2016,
    "pipeline_id": 375,
    "name": "Tentando Contato",
    "slug": "tentando-contato",
    "order_nr": 2,
    "color": "#6366f1",
    "leads_count": 0,
    "leads_open_count": 0,
    "total_amount": 0.00
  }
]`}
                  />
                </div>

                {/* Método B: Head / Count direto na tabela deals */}
                <div className="space-y-1 mt-3">
                  <h4 className="text-xs font-semibold text-blue-400">
                    Opção B: Contagem direta na tabela deals (sem baixar os
                    dados)
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Envie o cabeçalho HTTP{" "}
                    <code className="text-foreground font-mono">
                      Prefer: count=exact
                    </code>{" "}
                    e limite 0 resultados. O número total de leads virá no
                    cabeçalho{" "}
                    <code className="text-blue-400 font-mono">
                      content-range
                    </code>{" "}
                    da resposta (ex: <code>0-0/12</code> significa 12 leads):
                  </p>
                  <CodeBlock
                    title="Contar quantos leads estão em uma etapa específica"
                    code={`GET ${SUPABASE_URL}/rest/v1/deals?stage=eq.entrada-de-prospect&select=id&limit=1
Headers:
  apikey: ${SUPABASE_ANON_KEY}
  Authorization: Bearer <SEU_TOKEN>
  Prefer: count=exact`}
                    playgroundUrl="/api-playground"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA 3: ATIVIDADES DO NEGÓCIO & RELACIONAMENTOS (JOINS) ───────── */}
        <TabsContent value="atividades" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-400" />
                Como consultar Atividades do Negócio via API
              </CardTitle>
              <CardDescription className="text-xs">
                No CRM DOTUS, as atividades (tarefas, reuniões, ligações e
                compromissos) são armazenadas na tabela{" "}
                <code className="text-amber-400">tasks</code> vinculadas pelo
                campo <code className="text-amber-400">deal_id</code>. Anotações
                ficam em <code className="text-amber-400">deal_notes</code>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Opção 1 */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-amber-500/10 text-amber-400 border-amber-500/30"
                  >
                    Método Direto
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    1. Filtrar as atividades da tabela tasks pelo ID do Negócio
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Consulta simples à tabela{" "}
                  <code className="text-foreground font-mono">tasks</code>{" "}
                  passando o parâmetro{" "}
                  <code className="text-foreground font-mono">
                    deal_id=eq.&lt;ID&gt;
                  </code>
                  :
                </p>
                <CodeBlock
                  title="Todas as atividades do Negócio #1 ordenadas por data de início"
                  code={`GET ${SUPABASE_URL}/rest/v1/tasks?deal_id=eq.1&order=data_inicio.desc`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Opção 2 */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-violet-500/10 text-violet-400 border-violet-500/30"
                  >
                    Resource Embedding (JOIN)
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    2. Buscar o Negócio já com as Atividades Embutidas no mesmo
                    JSON
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  O PostgREST permite aninhar recursos relacionados em uma única
                  chamada HTTP usando a sintaxe{" "}
                  <code className="text-violet-400 font-mono">
                    select=*,tasks(*)
                  </code>
                  :
                </p>
                <CodeBlock
                  title="Negócio #1 completo com todas as tarefas e anotações aninhadas"
                  code={`GET ${SUPABASE_URL}/rest/v1/deals?id=eq.1&select=id,name,amount,stage,tasks(*),deal_notes(*)`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Opção 3 */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-blue-500/10 text-blue-400 border-blue-500/30"
                  >
                    JOIN Inverso
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    3. Listar Atividades trazendo o nome do Negócio e Contato
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground">
                  Você também pode consultar atividades e trazer os dados do
                  negócio vinculado:
                </p>
                <CodeBlock
                  title="Atividades pendentes com dados resumidos do Negócio e Empresa"
                  code={`GET ${SUPABASE_URL}/rest/v1/tasks?done_date=is.null&select=id,type,text,data_inicio,prioridade,deals(id,name,stage),companies(id,name)`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Criar Atividade para o Negócio (POST) */}
              <div className="space-y-2 border-t border-border/40 pt-4">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                  >
                    POST
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    4. Criar uma nova Atividade vinculada ao Negócio
                  </h3>
                </div>
                <CodeBlock
                  language="json"
                  title="Body para POST em /tasks"
                  code={`{
  "deal_id": 1,
  "type": "Meeting",
  "text": "Apresentação da Proposta Comercial",
  "prioridade": "alta",
  "data_inicio": "2026-10-05T14:00:00Z",
  "data_fim": "2026-10-05T15:00:00Z",
  "link_chamada": "https://meet.google.com/abc-defg-hij",
  "notes": "Alinhar prazos de implementação e pagamento."
}`}
                  playgroundUrl="/api-playground"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA 4: CUSTOM FIELDS (JSONB) ─────────────────────────────────── */}
        <TabsContent value="custom-fields" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-400" />
                Trabalhando com Custom Fields (Colunas JSONB)
              </CardTitle>
              <CardDescription className="text-xs">
                As tabelas <code className="text-foreground">contacts</code>,{" "}
                <code className="text-foreground">companies</code>,{" "}
                <code className="text-foreground">deals</code> e{" "}
                <code className="text-foreground">tasks</code> possuem a coluna{" "}
                <code className="text-violet-400">custom_fields</code> (JSONB)
                para atributos personalizados e flexíveis.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Extração de Texto */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <span className="font-mono text-violet-400 font-bold">
                    -&gt;&gt;
                  </span>
                  Extração de Texto do JSONB
                </h3>
                <p className="text-xs text-muted-foreground">
                  Use o operador{" "}
                  <code className="text-violet-400">-&gt;&gt;</code> para
                  extrair uma chave do JSONB como texto e aplicar qualquer
                  operador de filtro normal:
                </p>
                <CodeBlock
                  title="Buscar contatos cuja origem no custom_fields seja 'whatsapp'"
                  code={`GET ${SUPABASE_URL}/rest/v1/contacts?custom_fields->>origem=eq.whatsapp`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Comparação Numérica */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <span className="font-mono text-emerald-400 font-bold">
                    gt / gte / lt
                  </span>
                  Filtro por Valores Numéricos ou Datas dentro do JSONB
                </h3>
                <CodeBlock
                  title="Buscar empresas cujo score de crédito seja maior ou igual a 80"
                  code={`GET ${SUPABASE_URL}/rest/v1/companies?custom_fields->>score=gte.80`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Verificação de Chave Existente */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <span className="font-mono text-amber-400 font-bold">
                    not.is.null
                  </span>
                  Verificar se a chave existe no campo customizado
                </h3>
                <CodeBlock
                  title="Apenas registros que possuem o campo 'segmento' preenchido"
                  code={`GET ${SUPABASE_URL}/rest/v1/deals?custom_fields->segmento=not.is.null`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* JSON Contido (JSON Contains) */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <span className="font-mono text-blue-400 font-bold">
                    @&gt;
                  </span>
                  Operador de Contenção JSONB (JSON Contains)
                </h3>
                <p className="text-xs text-muted-foreground">
                  O operador <code className="text-blue-400">@&gt;</code>{" "}
                  verifica se o objeto JSONB contém a estrutura chave-valor
                  fornecida:
                </p>
                <CodeBlock
                  title='Buscar registros onde custom_fields contém { "vip": true }'
                  code={`GET ${SUPABASE_URL}/rest/v1/contacts?custom_fields=@>{"vip":true}`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* Inserir e Atualizar */}
              <div className="space-y-2 border-t border-border/40 pt-4">
                <h3 className="font-semibold text-sm text-foreground">
                  Como Salvar e Atualizar Custom Fields (POST / PATCH)
                </h3>
                <p className="text-xs text-muted-foreground">
                  Basta enviar um objeto JSON normal dentro do atributo{" "}
                  <code className="text-violet-400">custom_fields</code> no body
                  da requisição:
                </p>
                <CodeBlock
                  language="json"
                  title="Exemplo de payload com campos personalizados"
                  code={`{
  "name": "Expansão Filial Sul",
  "amount": 45000,
  "stage": "proposal-sent",
  "custom_fields": {
    "origem_lead": "indicacao_diretoria",
    "decisor_definido": true,
    "software_atual": "Legacy CRM",
    "potencial_mensal": 3500.00
  }
}`}
                  playgroundUrl="/api-playground"
                />
              </div>

              {/* API DE DEFINIÇÕES DE CUSTOM FIELDS */}
              <div className="space-y-4 border-t border-border/40 pt-6">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-violet-500/10 text-violet-400 border-violet-500/30"
                  >
                    Metadados & Identificadores
                  </Badge>
                  <h3 className="font-semibold text-sm text-foreground">
                    Como Puxar e Criar Identificadores de Custom Fields
                    (custom_field_definitions)
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Para saber quais campos existem e qual{" "}
                  <strong>
                    identificador (
                    <code className="text-violet-400 font-mono">key</code>)
                  </strong>{" "}
                  deve ser enviado no JSONB, consulte a tabela{" "}
                  <code className="text-foreground font-mono">
                    custom_field_definitions
                  </code>
                  . Você pode filtrar por{" "}
                  <code className="text-foreground font-mono">entity_type</code>{" "}
                  (deal, contact, company, task) e também criar novos campos
                  dinamicamente:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-violet-400 font-bold">
                      // Puxar campos de Negócios (Deals):
                    </p>
                    <p>
                      GET
                      /custom_field_definitions?entity_type=eq.deal&order=order_nr.asc
                    </p>
                    <p className="text-violet-400 font-bold mt-2">
                      // Puxar campos de Contatos (Pessoas):
                    </p>
                    <p>
                      GET
                      /custom_field_definitions?entity_type=eq.contact&order=order_nr.asc
                    </p>
                    <p className="text-violet-400 font-bold mt-2">
                      // Puxar campos de Empresas:
                    </p>
                    <p>
                      GET
                      /custom_field_definitions?entity_type=eq.company&order=order_nr.asc
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p className="text-blue-400 font-bold">
                      // Atualizar label/opções de um campo:
                    </p>
                    <p>PATCH /custom_field_definitions?id=eq.3</p>
                    <p className="text-red-400 font-bold mt-2">
                      // Excluir definição do campo:
                    </p>
                    <p>DELETE /custom_field_definitions?id=eq.3</p>
                  </div>
                </div>

                {/* LISTA DE FIELD_TYPES E REGRAS */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-violet-400" />
                    <h4 className="font-semibold text-sm text-foreground">
                      Lista de Tipos Disponíveis (
                      <code className="text-violet-400 font-mono text-xs">
                        field_type
                      </code>
                      ) e Regras
                    </h4>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Ao criar uma definição em{" "}
                    <code className="text-foreground font-mono">
                      custom_field_definitions
                    </code>
                    , defina o atributo{" "}
                    <code className="text-violet-400 font-mono">
                      field_type
                    </code>
                    . Abaixo estão todos os tipos aceitos pelo CRM e as regras
                    de preenchimento de cada um:
                  </p>

                  {/* Alerta de Regra para Options */}
                  <div className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-xs leading-relaxed">
                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <strong className="text-amber-300 font-semibold">
                        Regra de Ouro para o parâmetro{" "}
                        <code className="font-mono text-amber-200 bg-amber-950/60 px-1 py-0.5 rounded">
                          options
                        </code>
                        :
                      </strong>
                      <p className="mt-1 text-amber-200/90">
                        Quando o{" "}
                        <code className="font-mono text-amber-200">
                          field_type
                        </code>{" "}
                        for <strong>select</strong>,{" "}
                        <strong>multiselect</strong> ou{" "}
                        <strong>autocomplete</strong>, você{" "}
                        <strong>DEVE</strong> enviar o array com a lista de
                        opções desejadas no atributo{" "}
                        <code className="font-mono text-amber-200">
                          options
                        </code>{" "}
                        (ex:{" "}
                        <code className="font-mono text-amber-200 bg-amber-950/60 px-1 py-0.5 rounded">
                          ["Opção 1", "Opção 2", "Opção 3"]
                        </code>
                        ). Se omitido ou enviado vazio, o usuário não terá
                        opções para escolher na interface. Para todos os outros
                        tipos (como <code className="font-mono">text</code>,{" "}
                        <code className="font-mono">number</code>,{" "}
                        <code className="font-mono">currency</code>,{" "}
                        <code className="font-mono">date</code>,{" "}
                        <code className="font-mono">boolean</code>), envie{" "}
                        <code className="font-mono text-amber-200 bg-amber-950/60 px-1 py-0.5 rounded">
                          options: []
                        </code>
                        .
                      </p>
                    </div>
                  </div>

                  {/* Tabela de Tipos */}
                  <div className="rounded-lg border border-border/50 overflow-hidden bg-card/40">
                    <Table>
                      <TableHeader className="bg-muted/40">
                        <TableRow className="border-border/50 text-[11px]">
                          <TableHead className="font-semibold text-foreground w-[130px]">
                            field_type
                          </TableHead>
                          <TableHead className="font-semibold text-foreground w-[150px]">
                            Rótulo / Descrição
                          </TableHead>
                          <TableHead className="font-semibold text-foreground">
                            Regras & Parâmetro{" "}
                            <code className="font-mono text-violet-400 text-[10px]">
                              options
                            </code>
                          </TableHead>
                          <TableHead className="font-semibold text-foreground w-[180px]">
                            Valor no{" "}
                            <code className="font-mono text-violet-400 text-[10px]">
                              custom_fields
                            </code>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody className="text-xs divide-y divide-border/40">
                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            select
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Opção única
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Menu suspenso
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/30 mr-1.5 font-medium"
                            >
                              Requer options
                            </Badge>
                            Obrigatório enviar array com as alternativas:{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: ["Opção 1", "Opção 2"]
                            </code>
                            . No registro, armazena exatamente a string
                            selecionada.
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">"WhatsApp"</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            multiselect
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Múltipla escolha
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Tags ou checkboxes
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-amber-500/10 text-amber-300 border-amber-500/30 mr-1.5 font-medium"
                            >
                              Requer options
                            </Badge>
                            Obrigatório enviar o array de opções disponíveis. No
                            registro, armazena um array de strings ou texto com
                            itens selecionados.
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              ["Ads", "Indicação"]
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            autocomplete
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Autocompletar
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Sugestão com texto livre
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            <Badge
                              variant="outline"
                              className="text-[10px] bg-blue-500/10 text-blue-300 border-blue-500/30 mr-1.5 font-medium"
                            >
                              Requer options
                            </Badge>
                            Envia as sugestões no array{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options
                            </code>
                            . Permite que o operador escolha uma sugestão ou
                            digite texto personalizado.
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "Tecnologia SaaS"
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            text
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Texto simples
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Linha única
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Texto livre de linha única (até 255 caracteres).
                            Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "Diretoria Financeira"
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            textarea
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Texto longo
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Multilinha
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Texto livre multilinha para históricos, resumos e
                            anotações ricas. Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "Observações do lead..."
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            currency
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Monetário (R$)
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Valor financeiro
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Armazena número decimal ou inteiro no banco. O CRM
                            formata automaticamente como moeda brasileira{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              R$ 15.000,00
                            </code>
                            . Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-amber-400">15000.50</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            number
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Número
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Inteiro ou decimal
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Valores numéricos (ex: quantidade de usuários,
                            pontuação, score). Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-amber-400">42</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            date
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Data
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Calendário
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Formato ISO{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              YYYY-MM-DD
                            </code>
                            . No CRM é renderizado com seletor de calendário.
                            Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-blue-400">"2026-10-15"</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            time
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Horário
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Hora do dia
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Formato de hora padrão{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              HH:mm
                            </code>{" "}
                            (ex: 14:30). Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-blue-400">"14:30"</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            boolean
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Booleano (Sim/Não)
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Toggle / Switch
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Valores lógicos{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              true
                            </code>{" "}
                            ou{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              false
                            </code>
                            . Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-purple-400">true</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            email
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            E-mail
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Validação de e-mail
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Endereço de e-mail com link clicável{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              mailto:
                            </code>
                            . Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "contato@empresa.com"
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            tel
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Telefone / WhatsApp
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Com máscara
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Número de telefone ou WhatsApp com link direto para
                            conversa. Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "+5511987654321"
                            </span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            user
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Usuário da Equipe
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Vendedor / Responsável
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Armazena o ID numérico (
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              sales_id
                            </code>
                            ) de um usuário da equipe. Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-amber-400">12</span>
                          </TableCell>
                        </TableRow>

                        <TableRow className="hover:bg-muted/20">
                          <TableCell className="font-mono font-medium text-violet-400">
                            address
                          </TableCell>
                          <TableCell className="font-medium text-foreground">
                            Endereço
                            <span className="block text-[10px] text-muted-foreground font-normal">
                              Logradouro / Local
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[11px] leading-relaxed">
                            Endereço ou localização com link para mapas. Enviar{" "}
                            <code className="font-mono text-foreground bg-muted px-1 py-0.2 rounded text-[10px]">
                              options: []
                            </code>
                            .
                          </TableCell>
                          <TableCell className="font-mono text-[11px] text-zinc-300">
                            <span className="text-emerald-400">
                              "Av. Paulista, 1000"
                            </span>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="space-y-4 pt-2">
                  <h4 className="font-semibold text-sm text-foreground">
                    Exemplos Práticos de Criação (POST
                    /custom_field_definitions)
                  </h4>

                  <CodeBlock
                    language="json"
                    title="Exemplo 1: Criando campo de Opção Única com opções obrigatórias (field_type: 'select')"
                    code={`POST ${SUPABASE_URL}/rest/v1/custom_field_definitions
Content-Type: application/json

{
  "entity_type": "deal",
  "key": "origem_lead",
  "label": "Origem do Lead",
  "field_type": "select",
  "options": [
    "WhatsApp",
    "Google Ads",
    "Indicação de Cliente",
    "Evento Presencial",
    "Outros"
  ],
  "description": "Canal de aquisição principal do negócio",
  "required": false,
  "order_nr": 1
}`}
                    playgroundUrl="/api-playground"
                  />

                  <CodeBlock
                    language="json"
                    title="Exemplo 2: Criando campo monetário ou de data sem opções (field_type: 'currency' ou 'date')"
                    code={`POST ${SUPABASE_URL}/rest/v1/custom_field_definitions
Content-Type: application/json

{
  "entity_type": "deal",
  "key": "custo_implantacao",
  "label": "Custo de Implantação Estimado",
  "field_type": "currency",
  "options": [],
  "description": "Valor monetário estimado para serviços de setup",
  "required": false,
  "order_nr": 2
}`}
                    playgroundUrl="/api-playground"
                  />

                  <CodeBlock
                    language="json"
                    title="Exemplo 3: Atualizando as opções de um select existente (PATCH /custom_field_definitions?id=eq.1)"
                    code={`PATCH ${SUPABASE_URL}/rest/v1/custom_field_definitions?id=eq.1
Content-Type: application/json

{
  "options": [
    "WhatsApp",
    "Google Ads",
    "Indicação de Cliente",
    "Evento Presencial",
    "Prospecção Ativa (Outbound)",
    "Outros"
  ]
}`}
                    playgroundUrl="/api-playground"
                  />

                  <CodeBlock
                    language="json"
                    title="Exemplo de retorno ao consultar os campos (GET /custom_field_definitions?entity_type=eq.deal)"
                    code={`[
  {
    "id": 1,
    "entity_type": "deal",
    "key": "origem_lead",
    "label": "Origem do Lead",
    "field_type": "select",
    "options": ["WhatsApp", "Google Ads", "Indicação de Cliente", "Evento Presencial", "Outros"],
    "required": false,
    "order_nr": 1
  },
  {
    "id": 2,
    "entity_type": "deal",
    "key": "custo_implantacao",
    "label": "Custo de Implantação Estimado",
    "field_type": "currency",
    "options": [],
    "required": false,
    "order_nr": 2
  },
  {
    "id": 3,
    "entity_type": "deal",
    "key": "decisor_definido",
    "label": "Decisor Identificado?",
    "field_type": "boolean",
    "options": [],
    "required": false,
    "order_nr": 3
  }
]`}
                    playgroundUrl="/api-playground"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA 5: CATÁLOGO DE RECURSOS ──────────────────────────────────── */}
        <TabsContent value="recursos" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5 text-violet-400" />
                Catálogo de Tabelas e Recursos do CRM
              </CardTitle>
              <CardDescription className="text-xs">
                Todas as tabelas do CRM disponíveis na API REST com suporte a
                RLS por usuário autenticado.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  {
                    table: "pipelines",
                    name: "Funis (Pipelines)",
                    desc: "Funis de vendas customizados com ordenação e status padrão.",
                    fields:
                      "id, name, description, is_default, order_nr, created_at, updated_at",
                  },
                  {
                    table: "pipeline_stages",
                    name: "Etapas do Funil (Stages)",
                    desc: "Etapas de cada funil vinculadas por pipeline_id com slug, cor e ordenação.",
                    fields:
                      "id, pipeline_id, name, slug, order_nr, color, created_at, updated_at",
                  },
                  {
                    table: "custom_field_definitions",
                    name: "Definições de Custom Fields",
                    desc: "Identificadores (key), rótulos, tipos e opções de campos personalizados para deals, contacts, companies e tasks.",
                    fields:
                      "id, entity_type, key, label, field_type, options, required, order_nr",
                  },
                  {
                    table: "deals",
                    name: "Negócios (Pipeline)",
                    desc: "Oportunidades do Kanban com estágio, valor, empresa, funil_id, stage_id e previsão de fechamento.",
                    fields:
                      "id, name, stage, funil_id, stage_id, amount, company_id, category, expected_closing_date, custom_fields",
                  },
                  {
                    table: "tasks",
                    name: "Atividades & Tarefas",
                    desc: "Reuniões, ligações e compromissos vinculados a negócios (deal_id) ou contatos (contact_id).",
                    fields:
                      "id, deal_id, contact_id, company_id, type, text, data_inicio, data_fim, prioridade, done_date",
                  },
                  {
                    table: "deal_notes",
                    name: "Anotações do Negócio",
                    desc: "Histórico, anotações de cold call e reuniões anexadas ao negócio.",
                    fields:
                      "id, deal_id, type, text, date, sales_id, attachments",
                  },
                  {
                    table: "contacts",
                    name: "Contatos",
                    desc: "Pessoas, leads e clientes (telefones, e-mails, empresa e histórico de navegação).",
                    fields:
                      "id, first_name, last_name, company_id, status, email_jsonb, phone_jsonb, custom_fields",
                  },
                  {
                    table: "contact_notes",
                    name: "Anotações do Contato",
                    desc: "Notas registradas para o contato específico.",
                    fields: "id, contact_id, text, status, date, sales_id",
                  },
                  {
                    table: "companies",
                    name: "Empresas",
                    desc: "Empresas, setor, faturamento, website, endereço e campos customizados.",
                    fields:
                      "id, name, sector, size, website, phone_number, city, state_abbr, custom_fields",
                  },
                  {
                    table: "sales",
                    name: "Vendedores / Usuários",
                    desc: "Membros da equipe com acesso ao CRM.",
                    fields:
                      "id, first_name, last_name, email, administrator, user_id",
                  },
                  {
                    table: "tags",
                    name: "Etiquetas",
                    desc: "Tags e categorias visuais para contatos e negócios.",
                    fields: "id, name, color",
                  },
                ].map((rec) => (
                  <div
                    key={rec.table}
                    className="p-3.5 rounded-lg border border-border/50 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-violet-400 bg-violet-950/30 px-2 py-0.5 rounded border border-violet-500/20">
                          {rec.table}
                        </span>
                        <span className="font-semibold text-sm text-foreground">
                          {rec.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {rec.desc}
                      </p>
                      <p className="text-[11px] font-mono text-zinc-400">
                        <span className="text-zinc-500">Campos:</span>{" "}
                        {rec.fields}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className="shrink-0 text-xs gap-1"
                    >
                      <Link to="/api-playground">
                        Explorar <ArrowRight className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ABA 6: AUTENTICAÇÃO ─────────────────────────────────────────── */}
        <TabsContent value="autenticacao" className="space-y-6">
          <Card className="border-border/60 bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-violet-400" />
                Como Autenticar Requisições na API REST
              </CardTitle>
              <CardDescription className="text-xs">
                Entenda os headers obrigatórios e como usar sua chave de acesso
                em integrações (Zapier, n8n, Python, etc.).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <h3 className="font-semibold text-sm text-foreground">
                  Cabeçalhos HTTP Obrigatórios
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="p-3 rounded-lg bg-zinc-950 border border-border/40 font-mono space-y-1 text-zinc-300">
                    <p>
                      <span className="text-violet-400 font-bold">apikey:</span>{" "}
                      {SUPABASE_ANON_KEY}
                    </p>
                    <p>
                      <span className="text-violet-400 font-bold">
                        Authorization:
                      </span>{" "}
                      Bearer &lt;seu_token_jwt_ou_sessao&gt;
                    </p>
                    <p>
                      <span className="text-violet-400 font-bold">
                        Content-Type:
                      </span>{" "}
                      application/json
                    </p>
                    <p>
                      <span className="text-violet-400 font-bold">Prefer:</span>{" "}
                      return=representation (para POST/PATCH retornar o
                      registro)
                    </p>
                  </div>
                </div>
              </div>

              {/* Criptografia Hash */}
              <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-950/10 space-y-2">
                <h3 className="font-semibold text-sm text-foreground flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs"
                  >
                    Segurança
                  </Badge>
                  Criptografia Hash SHA-256 das API Keys
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Quando você gera uma API Key (
                  <code className="text-foreground">crm_...</code>) na aba{" "}
                  <strong>Perfil</strong>, apenas o hash criptográfico SHA-256 é
                  armazenado no banco de dados. A chave original nunca é exposta
                  nem guardada em texto puro.
                </p>
              </div>

              {/* Exemplo de Chamada em Python */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground">
                  Exemplo em Python (requests)
                </h3>
                <CodeBlock
                  language="python"
                  title="script.py"
                  code={`import requests

URL = "${SUPABASE_URL}/rest/v1/deals"
HEADERS = {
    "apikey": "${SUPABASE_ANON_KEY}",
    "Authorization": "Bearer <SEU_TOKEN_JWT>",
    "Content-Type": "application/json"
}

# Buscar negócios com tarefas embutidas
params = {
    "select": "id,name,amount,stage,tasks(*)",
    "order": "amount.desc",
    "limit": "10"
}

response = requests.get(URL, headers=HEADERS, params=params)
deals = response.json()

for deal in deals:
    print(f"Negócio: {deal['name']} - R$ {deal['amount']} ({len(deal.get('tasks', []))} atividades)")`}
                />
              </div>

              {/* Exemplo em JavaScript / Node.js */}
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-foreground">
                  Exemplo em JavaScript (Node.js / Browser fetch)
                </h3>
                <CodeBlock
                  language="javascript"
                  title="fetchDeals.js"
                  code={`const url = "${SUPABASE_URL}/rest/v1/tasks?deal_id=eq.1&order=data_inicio.desc";

fetch(url, {
  method: "GET",
  headers: {
    "apikey": "${SUPABASE_ANON_KEY}",
    "Authorization": "Bearer <SEU_TOKEN_JWT>",
    "Content-Type": "application/json",
  }
})
  .then(res => res.json())
  .then(tasks => console.log("Atividades do Negócio:", tasks))
  .catch(console.error);`}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

ApiDocs.path = "/api-docs";
